import { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import {
  RainfallChart,
  VegetronixVH400Chart,
  WindDirectionChart,
  GenericLineChart,
  FlowMeterChart,
  UnifiedChartVegetronixVH400,
  UnifiedChartIrrometer,
  VegetronixTHERM200Chart,
  UnifiedChartVegetronixTHERM200,
  BatteryLineChart,
  ComparisonChart,
  AlertMarkersToggle,
} from '../components/charts'
import { useAlertChartMarkers, useAlertMarkersVisibility } from '../hooks'
import { ChartEditWrapper } from '../components/ChartEditWrapper'
import { hasHiddenEntries } from '../utils/chartHelpers'
import { useAccountContext, useAppContext } from '../contexts'
import {
  ChartSubHeader,
  CollapsibleSection,
  DeviceAlertSettings,
  DeviceDetails,
  DeviceSiteDetails,
  TimeRangeNavigation,
  TimeRange,
  CompareModeBanner,
  SensorCompareGrid,
} from '../components'
import type { DeviceSensorInfo, ComparableSensorType } from '../components'
import { Spinner } from '../components/Spinner'
import {
  AlertSettingsItem,
  AlertTypeEnums,
  ALERT_SETTINGS_GLOBAL_DEVICE_ID,
  SCHD_INTERVAL_MINUTES,
} from '../common/constants'
import { getAlertSettingsFromBackend } from '../services/alertSettings'
import { api } from '../services'
import { SensorProduct, UnitCategoryId } from '../common/enums'
import { DeviceSensorSettings } from '../components/DeviceSensorSettings'
import { DeviceControlPanel } from '../components/DeviceControlPanel'
import { BadgerSDIChart } from '../components/charts/BadgerSDIChart'
import { Viewport } from '../common/types'
import { useDeviceUrlSync } from '../hooks/useDeviceUrlSync'
import { useCompareUrlSync } from '../hooks/useCompareUrlSync'
import { useCompareChartView } from '../hooks/useCompareChartView'
import { seriesId } from '../components/charts/compareSeries'
import { useCompareToggle } from '../hooks/useCompareToggle'
import { TestReadingsCache } from '../components/TestReadingsCache'
import { useDeviceReadings, useBatteryReadings } from '../hooks/useDeviceReadings'
import { useMultiDeviceReadings, useMultiDeviceSensorConfigs } from '../hooks/useMultiDeviceReadings'
import { ExportModal } from '../components/export'
import { useQueryClient } from '@tanstack/react-query'
import { FEATURE_FLAGS } from '../utils/featureFlags'

/**
 * Shared empty marker list. The CanvasJS wrapper repaints on any change of
 * `options` identity, so props that feed the options object must keep a stable
 * identity when their value has not actually changed.
 */
const NO_ALERT_MARKERS: never[] = []

enum RicMode {
  Standard = 'Standard',
  WeatherStation = 'WeatherStation',
  WaterQuality = 'WaterQuality',
  DirtTracker = 'DirtTracker',
  FlowMeter = 'FlowMeter',
  Unknown = 'Unknown',
}

export const RicModeLookup: Record<number, RicMode> = {
  0: RicMode.Standard,
  1: RicMode.WeatherStation,
  2: RicMode.WaterQuality,
  3: RicMode.DirtTracker,
  1000: RicMode.FlowMeter, // there is official value for FlowMeter so we use 1000
  5000: RicMode.Unknown,
}

interface Props {
  sigfoxId?: string
  viewport?: Viewport
  initialTimeRange?: TimeRange
  onTimeRangeChange?: (range: TimeRange) => void
}

export function ChartsPage({
  sigfoxId: propSigfoxId,
  viewport: externalViewport,
  initialTimeRange,
  onTimeRangeChange,
}: Props) {
  // Get sigfoxId and time range from URL if not provided as prop (for standalone /charts route)
  const { sigfoxId: urlSigfoxId, urlTimeRange, updateTimeRangeInUrl } = useDeviceUrlSync()
  // Called for its side effects: keeps compare state and the /compare URL in sync.
  useCompareUrlSync()
  const toggleCompareMode = useCompareToggle()
  const sigfoxId = propSigfoxId || urlSigfoxId || ''

  const {
    showBatteryCharts,
    setShowBatteryCharts,
    showTemperatureCharts,
    setShowTemperatureCharts,
    currentDate,
    setCurrentDate,
    // Compare Mode
    compareMode,
    compareVariant,
    selectedDevicesForComparison,
    selectedSensorType,
    setSelectedSensorType,
    selectedSubType,
    setSelectedSubType,
    selectedSensors,
    toggleSensorWithType,
    toggleSensorForDevice,
    toggleSensorsBulk,
    switchSensorType,
    setCompareVariant,
    clearSensorSelections,
  } = useAppContext()
  const { devices, userUnitSettings, unitConversions } = useAccountContext()
  const queryClient = useQueryClient()

  // Track if this is the initial mount (to avoid invalidating on first render)
  const isInitialMount = useRef(true)

  // Reset battery and temperature charts view when navigating away from the charts page
  useEffect(() => {
    return () => {
      setShowBatteryCharts(false)
      setShowTemperatureCharts(true)
    }
  }, [setShowBatteryCharts, setShowTemperatureCharts])

  // Honor ?battery=1 in the URL so refreshing on the battery view restores it.
  // Useful for shareable links (e.g. the Dashboard's Battery card jumps here
  // with the flag set so the user lands directly on the battery chart).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('battery') === '1') {
      setShowBatteryCharts(true)
    }
  }, [setShowBatteryCharts])

  // Calculate day count label from timestamps
  const calculateLabel = (fromMs: number, toMs: number): string => {
    const days = Math.round((toMs - fromMs) / (24 * 60 * 60 * 1000))
    return `${days} day${days !== 1 ? 's' : ''}`
  }

  // Internal time range state for standalone mode
  // Priority: initialTimeRange prop > URL params > default (28 days)
  const [timeRange, setTimeRange] = useState<TimeRange>(() => {
    if (initialTimeRange) {
      return initialTimeRange
    }
    if (urlTimeRange) {
      return {
        preset: 'month',
        from: urlTimeRange.from,
        to: urlTimeRange.to,
        label: calculateLabel(urlTimeRange.from, urlTimeRange.to),
      }
    }
    return {
      preset: 'month',
      from: Date.now() - 28 * 24 * 60 * 60 * 1000,
      to: Date.now(),
      label: '28 days',
    }
  })

  // Sync default time range to URL on initial load (standalone mode only)
  useEffect(() => {
    if (!externalViewport && !urlTimeRange && timeRange.from && timeRange.to) {
      updateTimeRangeInUrl(timeRange.from, timeRange.to)
    }
    // Only run on initial mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Use external viewport if provided, otherwise use time range
  // Memoize to prevent infinite re-renders
  const viewport: Viewport = useMemo(() => {
    return (
      externalViewport || {
        minimum: timeRange.from || Date.now() - 28 * 24 * 60 * 60 * 1000,
        maximum: timeRange.to || Date.now(),
      }
    )
  }, [externalViewport, timeRange.from, timeRange.to])

  // Calculate time range and limit for data fetching
  const from = useMemo(() => Math.round(viewport.minimum / 1000), [viewport.minimum])
  const to = useMemo(() => Math.round(viewport.maximum / 1000), [viewport.maximum])
  const limit = useMemo(() => {
    const totalDays = (to - from) / (60 * 60 * 24)
    const numberOfDataPoints = totalDays * 24 * (60 / SCHD_INTERVAL_MINUTES)
    return Math.round(numberOfDataPoints * 2)
  }, [from, to])

  // Use hooks for data fetching with caching
  // isFetching is true during refetches (date changes), isLoading only on initial load
  const {
    data: readingsData,
    isLoading: isReadingsLoading,
    isFetching: isReadingsFetching,
    loadedWindows,
    totalWindows,
  } = useDeviceReadings(sigfoxId, from, to, limit, !!sigfoxId)

  // Only fetch battery readings when battery charts are shown
  const {
    data: batteryData,
    isLoading: isBatteryLoading,
    isFetching: isBatteryFetching,
  } = useBatteryReadings(sigfoxId, from, to, undefined, !!sigfoxId && showBatteryCharts)

  // Extract readings and battery data from hook responses
  const readings = readingsData?.sensors
  const batteryReadings = batteryData?.data
  // Two different questions, and the charts answer them differently:
  // `isLoading` is "there is nothing to draw yet" — readings arrive one time
  // window at a time, so this clears as soon as the first (most recent) window
  // lands and the charts can start drawing. `isStillFetching` stays true until
  // the last window is in, and only drives the keep-loading indicator.
  const isLoading = isReadingsLoading || (showBatteryCharts && isBatteryLoading)
  const isStillFetching = isReadingsFetching || (showBatteryCharts && isBatteryFetching)

  const [sensorConfigs, setSensorConfigs] = useState<Record<string, any>>({})

  const [globalAlertSettings, setGlobalAlertSettings] = useState<AlertSettingsItem[]>([])
  const [currentTime, setCurrentTime] = useState(new Date().getTime())
  const [deviceConfig, setDeviceConfig] = useState<any>()
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)

  const device = devices[sigfoxId]

  // Alert markers (Phase 1: battery alerts only; DEVICE_OFFLINE lands in Phase 2)
  const { visible: alertMarkersVisible } = useAlertMarkersVisibility()
  const alertMarkersDateFrom = useMemo(() => new Date(viewport.minimum).toISOString(), [viewport.minimum])
  const alertMarkersDateTo = useMemo(() => new Date(viewport.maximum).toISOString(), [viewport.maximum])
  const allAlertMarkers = useAlertChartMarkers(device?.deviceId, alertMarkersDateFrom, alertMarkersDateTo)

  // Keyed on content rather than array identity. The markers hook publishes an
  // equal array more than once per load, and since these pools feed the chart
  // options, a new identity on its own repaints every canvas.
  const alertMarkersSignature = useMemo(
    () => allAlertMarkers.map((m: any) => `${m.alertType}:${m.timestamp}`).join('|'),
    [allAlertMarkers]
  )

  const batteryMarkerPool = useMemo(() =>
    allAlertMarkers.filter((m) =>
      m.alertType === AlertTypeEnums.BATTERY_LOW ||
      m.alertType === AlertTypeEnums.BATTERY_NO_POWER ||
      m.alertType === AlertTypeEnums.BATTERY_RECOVERY
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alertMarkersSignature]
  )
  const offlineMarkerPool = useMemo(() =>
    allAlertMarkers.filter((m) =>
      m.alertType === AlertTypeEnums.DEVICE_OFFLINE ||
      m.alertType === AlertTypeEnums.DEVICE_ONLINE_RECOVERY
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alertMarkersSignature]
  )
  // NO_ALERT_MARKERS is shared rather than a fresh `[]`: these are passed
  // straight into the chart options, and a new array identity is enough to make
  // the CanvasJS wrapper repaint the whole canvas.
  const batteryAlertMarkers = alertMarkersVisible ? batteryMarkerPool : NO_ALERT_MARKERS
  const offlineAlertMarkers = alertMarkersVisible ? offlineMarkerPool : NO_ALERT_MARKERS

  // ===== COMPARE MODE LOGIC =====

  // Fetch sensor configs for all selected devices in compare mode
  const { sensorConfigs: multiDeviceSensorConfigs } = useMultiDeviceSensorConfigs(
    selectedDevicesForComparison,
    to
  )

  // Build device sensor info array for SensorTypeSelector
  const deviceSensorInfos: DeviceSensorInfo[] = useMemo(() => {
    return selectedDevicesForComparison.map((deviceId) => ({
      deviceId,
      deviceName: devices[deviceId]?.deviceName || deviceId,
      siteName: devices[deviceId]?.siteName,
      sensorConfigs: multiDeviceSensorConfigs[deviceId] || {},
    }))
  }, [selectedDevicesForComparison, devices, multiDeviceSensorConfigs])

  // Build device names map for chart labels
  const deviceNamesMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const deviceId of selectedDevicesForComparison) {
      map[deviceId] = devices[deviceId]?.deviceName || deviceId
    }
    return map
  }, [selectedDevicesForComparison, devices])

  // Fetch multi-device readings when compare mode is active with sensor type selected
  const { deviceSensorData, isLoading: isCompareReadingsLoading } = useMultiDeviceReadings(
    selectedDevicesForComparison,
    selectedSensorType as ComparableSensorType | null,
    selectedSensors,
    viewport,
    deviceNamesMap,
    multiDeviceSensorConfigs,
    selectedSubType,
    compareVariant
  )

  // Determine if we should show compare chart (have devices and at least one sensor selected)
  const totalSelectedSensors = selectedSensors.reduce((sum, s) => sum + s.sensorPorts.length, 0)
  // Mixed mode works with a single device; single mode still needs two to compare.
  const minCompareDevices = compareVariant === 'mixed' ? 1 : 2
  const showCompareView =
    compareMode && selectedDevicesForComparison.length >= minCompareDevices && totalSelectedSensors > 0

  // Combined | Separate view, shared zoom and page; kept here because
  // ComparisonChart remounts on every refetch
  const compareSeriesIds = useMemo(() => deviceSensorData.map(seriesId), [deviceSensorData])
  const compareChartView = useCompareChartView({
    isActive: showCompareView,
    isReady: !isCompareReadingsLoading,
    seriesIds: compareSeriesIds,
    viewport,
  })

  // ===== END COMPARE MODE LOGIC =====

  // Refresh charts when currentDate changes (triggered by Refresh Charts button)
  useEffect(() => {
    // Skip the initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // When currentDate changes, invalidate queries to force re-fetch
    if (currentDate && sigfoxId) {
      // Invalidate all readings queries for this device to force fresh data
      queryClient.invalidateQueries({ queryKey: ['readings', sigfoxId] })
      queryClient.invalidateQueries({ queryKey: ['battery-readings', sigfoxId] })
    }
  }, [currentDate, sigfoxId, queryClient])

  useEffect(() => {
    getAlertSettingsFromBackend(ALERT_SETTINGS_GLOBAL_DEVICE_ID, AlertTypeEnums.SENSOR_ALERTS)
      .then((settings) => setGlobalAlertSettings(settings))
      .catch((error) => console.error(error))
    return () => {}
  }, [])

  useEffect(() => {
    if (!sigfoxId) return

    api
      .getDeviceConfig(sigfoxId)
      .then((response) => {
        setDeviceConfig(response.config)
      })
      .catch((error) => console.error(error))
  }, [sigfoxId])

  // Fetch sensor configs separately (not cached yet)
  useEffect(() => {
    if (!sigfoxId || !to) return

    setCurrentTime(new Date().getTime())
    api
      .getSensorConfigs(sigfoxId, to)
      .then((response) => {
        setSensorConfigs(response.data)
      })
      .catch((error) => console.error(error))
  }, [sigfoxId, to])

  const handleTimeRangeChange = (newRange: TimeRange) => {
    setTimeRange(newRange)
    if (onTimeRangeChange) {
      onTimeRangeChange(newRange)
    }
    // Sync to URL in standalone mode (when not using external viewport)
    if (!externalViewport && newRange.from && newRange.to) {
      updateTimeRangeInUrl(newRange.from, newRange.to)
    }
  }

  // Trigger chart refresh
  const handleRefresh = () => {
    if (sigfoxId) {
      setCurrentDate(new Date())
    }
  }

  // Toggle battery charts
  const handleToggleBatteryCharts = () => {
    setShowBatteryCharts(!showBatteryCharts)
  }

  // Toggle temperature charts
  const handleToggleTemperatureCharts = () => {
    setShowTemperatureCharts(!showTemperatureCharts)
  }

  // Open export modal
  const handleExport = useCallback(() => {
    setIsExportModalOpen(true)
  }, [])

  // Close export modal
  const handleCloseExportModal = useCallback(() => {
    setIsExportModalOpen(false)
  }, [])

  // Show temperature toggle for all modes EXCEPT WeatherStation and WaterQuality
  // (those modes already show temperature by default and don't need a toggle)
  const showTemperatureToggle = useMemo(() => {
    if (!deviceConfig || !readings) return true // Default to showing the toggle
    const mode = determineRicMode(deviceConfig, readings)
    const shouldShow = mode !== RicMode.WeatherStation && mode !== RicMode.WaterQuality
    return shouldShow
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceConfig, readings])

  function renderAllCharts(readings: any): JSX.Element[] {
    if (isLoading) return []

    const charts: JSX.Element[] = []

    // Only block on missing sensorConfigs and unitConversions - readings can be undefined/empty
    if (sensorConfigs === undefined || Object.keys(unitConversions).length === 0) {
      return []
    }

    // deviceConfig decides the RIC mode, and the mode decides which charts exist
    // at all. Rendering before it lands means drawing one set of charts and then
    // replacing them — a full repaint of every canvas — the moment it arrives.
    // It is fetched on mount alongside everything else, so this waits on the
    // slowest of the three rather than adding a round-trip.
    if (deviceConfig === undefined) {
      return []
    }

    // If no sensor configs, show a message
    if (Object.keys(sensorConfigs).length === 0) {
      return [
        <div key="no-sensors" className={tailwind.alert}>
          No sensors configured for this device
        </div>,
      ]
    }

    // readings can be undefined or empty - chart components will handle "no data" display
    const safeReadings = readings || {}

    const mode = determineRicMode(deviceConfig, safeReadings)

    const data: Record<string, any> = {}
    let order: any[] = []

    if (mode === RicMode.FlowMeter) {
      const temperaturePorts: string[] = []

      Object.keys(safeReadings).forEach((key: any) => {
        const [sensorPort] = key.split('-')

        if (data[sensorPort] === undefined) {
          data[sensorPort] = []
          // Separate temperature sensors to add at the end
          if (sensorPort === 'T0') {
            if (showTemperatureCharts) {
              temperaturePorts.push(sensorPort)
            }
          } else {
            order.push(sensorPort)
          }
        }

        data[sensorPort].push(safeReadings[key])
      })

      // Add temperature sensors at the end (after flow meters)
      order.push(...temperaturePorts)

      order.forEach((sensorPort) => {
        // Skip if no data exists for this sensor port
        if (!data[sensorPort] || data[sensorPort].length === 0) {
          return
        }

        const unitCategoryId = data[sensorPort][0].metadata?.units?.category?.id
        const chart: JSX.Element | null | undefined =
          data[sensorPort].length > 1
            ? renderUnifiedCharts(data[sensorPort])
            : renderIndividualChart(unitCategoryId, data[sensorPort][0])

        if (chart) {
          charts.push(chart)
        }
      })

      // If no charts were rendered from readings but we have configured sensors,
      // render placeholder charts showing "no data"
      if (charts.length === 0 && Object.keys(sensorConfigs).length > 0) {
        Object.keys(sensorConfigs).forEach((port) => {
          if (port === 'T0') return
          const config = sensorConfigs[port] as any
          const chart = renderPlaceholderChart(port, config.sensorProductName)
          if (chart) {
            charts.push(chart)
          }
        })
      }

      return charts
    } else {
      // Track temperature unit categories to add at the end (for Standard mode)
      const temperatureCategories: number[] = []

      Object.keys(safeReadings).forEach((key: any) => {
        const [sensorPort, unitCategoryId] = key.split('-')

        const metadata = safeReadings[key].metadata
        if (metadata.productName !== 'none') {
          if (data[unitCategoryId] === undefined) {
            data[unitCategoryId] = []
            // Check if this is a temperature sensor
            const isTempSensor =
              sensorPort === 'T0' ||
              Number(unitCategoryId) === UnitCategoryId.SoilTemperature ||
              Number(unitCategoryId) === UnitCategoryId.Temperature
            if (mode !== RicMode.WeatherStation) {
              if (isTempSensor) {
                // Collect temperature categories to add at end (if toggle is ON)
                if (showTemperatureCharts) {
                  temperatureCategories.push(Number(unitCategoryId))
                }
              } else {
                // Add non-temperature sensors to order immediately
                order.push(Number(unitCategoryId))
              }
            }
          }
          data[unitCategoryId].push(safeReadings[key])
        }
      })

      switch (mode) {
        case RicMode.WeatherStation:
          // Combine wind direction and speed data if both exist
          if (data[UnitCategoryId.WindDirection] && data[UnitCategoryId.WindSpeed]?.[0]) {
            data[UnitCategoryId.WindDirection].push(data[UnitCategoryId.WindSpeed][0])
          }
          order = [
            UnitCategoryId.Temperature,
            UnitCategoryId.Rainfall,
            UnitCategoryId.WindDirection,
            UnitCategoryId.WindSpeed,
            UnitCategoryId.Humidity,
            UnitCategoryId.Pressure,
            UnitCategoryId.SolarRadiation,
          ]
          break

        case RicMode.WaterQuality:
          if (data[UnitCategoryId.Temperature]) {
            data[UnitCategoryId.WaterTemperature] = data[UnitCategoryId.Temperature]
          }
          order = order.filter((sensorProduct) => sensorProduct !== UnitCategoryId.Temperature)
          order.push(UnitCategoryId.WaterTemperature)
          break

        default:
          // Standard mode: add temperature charts at the end (after soil moisture, etc.)
          order.push(...temperatureCategories)
          break
      }

      order.forEach((unitCategoryId) => {
        // Skip if no data exists for this unit category
        if (!data[unitCategoryId] || data[unitCategoryId].length === 0) {
          return
        }

        const chart: JSX.Element | null =
          data[unitCategoryId].length > 1 ||
          ['Vegetronix VH400', 'Vegetronix THERM200', 'Irrometer WM200SS'].includes(
            data[unitCategoryId][0].metadata.productName
          )
            ? renderUnifiedCharts(data[unitCategoryId])
            : renderIndividualChart(unitCategoryId, data[unitCategoryId][0])

        if (chart) {
          charts.push(chart)
        }
      })

      // If no charts were rendered from readings but we have configured sensors,
      // render placeholder charts showing "no data"
      // Group soil moisture sensors (Irrometer, Vegetronix VH400) into unified placeholders
      if (charts.length === 0 && Object.keys(sensorConfigs).length > 0) {
        // Group sensors by type
        const sensorsByType: Record<string, string[]> = {}
        Object.keys(sensorConfigs).forEach((port) => {
          if (port === 'T0') return // Skip temperature sensor
          const config = sensorConfigs[port] as any
          const productName = config.sensorProductName || 'Unknown'
          if (!sensorsByType[productName]) {
            sensorsByType[productName] = []
          }
          sensorsByType[productName].push(port)
        })

        // Render unified placeholder for soil moisture types, individual for others
        Object.keys(sensorsByType).forEach((productName) => {
          const ports = sensorsByType[productName]
          if (
            productName === 'Irrometer WM200SS' ||
            productName === 'Vegetronix VH400' ||
            productName === 'Vegetronix THERM200'
          ) {
            // Create unified placeholder for soil moisture sensors
            const chart = renderUnifiedPlaceholder(productName, ports, sensorConfigs)
            if (chart) {
              charts.push(chart)
            }
          } else {
            // Individual placeholders for other sensor types
            ports.forEach((port) => {
              const chart = renderPlaceholderChart(port, productName)
              if (chart) {
                charts.push(chart)
              }
            })
          }
        })
      }

      return charts
    }
  }

  function renderPlaceholderChart(sensorPort: string, productName: string): JSX.Element | null {
    // Render a placeholder chart for configured sensors without readings data
    const key = `placeholder-${sensorPort}-${productName?.replace(/\s+/g, '')}`

    return (
      <GenericLineChart
        key={key}
        device={device}
        isLoading={false}
        sensorProduct={productName as SensorProduct}
        sensorPort={sensorPort}
        sensorReadings={undefined}
        viewport={viewport}
        userUnitSettings={userUnitSettings}
        unitConversions={unitConversions}
        alertMarkers={offlineAlertMarkers}
      />
    )
  }

  function renderUnifiedPlaceholder(productName: string, ports: string[], configs: Record<string, any>): JSX.Element {
    // Render a unified placeholder for soil moisture sensors when all have no data
    const key = `unified-placeholder-${productName?.replace(/\s+/g, '')}`
    const chartTitle = productName === 'Vegetronix THERM200' ? 'Soil Temperature' : 'Soil Moisture'

    // Build sub-header data for all sensors in this group
    const subHeaderData = ports.map((port) => {
      const config = configs[port]
      const depth = config?.offset ? `Depth: ${config.offset}"` : ''
      return [`Port ${port}: ${productName}`, 'No readings', depth].filter(Boolean)
    })

    return (
      <CollapsibleSection key={key} title={chartTitle} open={true} isLoading={false}>
        <ChartSubHeader data={subHeaderData} />
        <div className="flex items-center justify-center py-8">
          <div className="bg-red-500 text-white font-bold rounded px-4 py-2 mb-10 mt-6 h-10 w-[320px] h-[70px] mx-auto text-center">
            This RIC has no data for the selected period
          </div>
        </div>
      </CollapsibleSection>
    )
  }

  function renderBatteryCharts(batteryReadings: any): JSX.Element[] {
    if (batteryReadings) {
      return Object.keys(batteryReadings).map((key) => {
        return (
          <BatteryLineChart
            key={key}
            device={device}
            isLoading={isLoading}
            chartName={key === 'battery' ? 'Battery' : 'Port C Power'}
            batteryReadings={batteryReadings[key]}
            viewport={viewport}
            userUnitSettings={userUnitSettings}
            unitConversions={unitConversions}
            alertMarkers={batteryAlertMarkers}
          />
        )
      })
    }

    return [<div key="empty"></div>]
  }

  function renderIndividualChart(unitCategoryId: number, readings: any) {
    const sensorPort = readings.metadata.sensorName
    const sensorProductName = readings.metadata.productName
    const sensorHasHiddenData = hasHiddenEntries(readings.hidden)

    const sensorProduct =
      unitCategoryId === UnitCategoryId.WaterTemperature
        ? SensorProduct.AquaTroll500TemperatureSensor
        : Object.values(SensorProduct).find((sensorProduct) =>
            sensorProductName ? sensorProduct.includes(sensorProductName) : false
          )

    const key = `${sensorPort}-${sensorProduct?.split(' ').join('')}`

    switch (sensorProduct) {
      case SensorProduct.AquaTroll500DissolvedOxygenSensor:
      case SensorProduct.AquaTroll500NitrateSensor:
      case SensorProduct.AquaTroll500SalinitySensor:
      case SensorProduct.AquaTroll500SpecificConductivitySensor:
      case SensorProduct.AquaTroll500TotalDissolvedSolidsSensor:
      case SensorProduct.AquaTroll500TurbiditySensor:
      case SensorProduct.AquaTroll500TemperatureSensor:
      case SensorProduct.Battery:
      case SensorProduct.Humidity:
      case SensorProduct.Pressure:
      case SensorProduct.SolarRadiation:
      case SensorProduct.Temperature:
      case SensorProduct.WindSpeed: {
        const isEditDisabled = sensorProduct === SensorProduct.Temperature
        return (
          <ChartEditWrapper
            key={key}
            sigfoxId={sigfoxId}
            sensorPort={sensorPort}
            sensorName={`${sensorPort}: ${sensorProductName || sensorProduct}`}
            hasHiddenData={sensorHasHiddenData}
            editDisabled={isEditDisabled}
            editDisabledReason={isEditDisabled ? 'Editing is not available for temperature sensors' : undefined}
          >
            {(editModeProps) => (
              <GenericLineChart
                device={device}
                isLoading={isLoading}
                sensorProduct={sensorProduct}
                sensorPort={sensorPort}
                sensorReadings={readings}
                viewport={viewport}
                userUnitSettings={userUnitSettings}
                unitConversions={unitConversions}
                alertMarkers={offlineAlertMarkers}
                {...editModeProps}
              />
            )}
          </ChartEditWrapper>
        )
      }

      case SensorProduct.Rainfall:
      case SensorProduct.OpticalRainSensorSAB:
      case SensorProduct.OpticalRainSensorPower:
        return (
          <ChartEditWrapper
            key={key}
            sigfoxId={sigfoxId}
            sensorPort={sensorPort}
            sensorName={`${sensorPort}: ${sensorProductName || sensorProduct}`}
            hasHiddenData={sensorHasHiddenData}
          >
            {(editModeProps) => (
              <RainfallChart
                device={device}
                sensorProduct={sensorProduct}
                sensorPort={sensorPort}
                sensorReadings={readings}
                viewport={viewport}
                userUnitSettings={userUnitSettings}
                unitConversions={unitConversions}
                isLoading={isLoading}
                alertMarkers={offlineAlertMarkers}
                {...editModeProps}
              />
            )}
          </ChartEditWrapper>
        )

      case SensorProduct.VegetronixVH400:
        return (
          <ChartEditWrapper
            key={key}
            sigfoxId={sigfoxId}
            sensorPort={sensorPort}
            sensorName={`${sensorPort}: ${sensorProductName || sensorProduct}`}
            hasHiddenData={sensorHasHiddenData}
          >
            {(editModeProps) => (
              <VegetronixVH400Chart
                device={device}
                sensorPort={sensorPort}
                sensorReadings={readings}
                viewport={viewport}
                isLoading={isLoading}
                userUnitSettings={userUnitSettings}
                unitConversions={unitConversions}
                alertMarkers={offlineAlertMarkers}
                {...editModeProps}
              />
            )}
          </ChartEditWrapper>
        )

      case SensorProduct.VegetronixTHERM200:
        return (
          <ChartEditWrapper
            key={key}
            sigfoxId={sigfoxId}
            sensorPort={sensorPort}
            sensorName={`${sensorPort}: ${sensorProductName || sensorProduct}`}
            hasHiddenData={sensorHasHiddenData}
          >
            {(editModeProps) => (
              <VegetronixTHERM200Chart
                device={device}
                sensorPort={sensorPort}
                sensorReadings={readings}
                viewport={viewport}
                isLoading={isLoading}
                userUnitSettings={userUnitSettings}
                unitConversions={unitConversions}
                alertMarkers={offlineAlertMarkers}
                {...editModeProps}
              />
            )}
          </ChartEditWrapper>
        )

      case SensorProduct.BadgermeterSDI:
        return (
          <ChartEditWrapper
            key={key}
            sigfoxId={sigfoxId}
            sensorPort={sensorPort}
            sensorName={`${sensorPort}: ${sensorProductName || sensorProduct}`}
            hasHiddenData={sensorHasHiddenData}
          >
            {(editModeProps) => (
              <BadgerSDIChart
                device={device}
                sigfoxId={sigfoxId}
                isLoading={isLoading}
                sensorPort={sensorPort}
                sensorProduct={sensorProduct}
                sensorReadings={[readings]}
                viewport={viewport}
                currentTime={currentTime}
                userUnitSettings={userUnitSettings}
                alertMarkers={offlineAlertMarkers}
                {...editModeProps}
              />
            )}
          </ChartEditWrapper>
        )

      default:
        // do nothing
        break
    }
    return null
  }

  function renderUnifiedCharts(readings: any[]) {
    const sensorType = getAllSensors(readings)[0]
    const sensorPort = readings[0].metadata.sensorName
    const unifiedHasHiddenData = readings.some((r: any) => hasHiddenEntries(r.hidden))

    switch (sensorType) {
      case 'Vegetronix VH400': {
        const sensorPorts = readings.map((r: any) => ({
          port: r.metadata.sensorName,
          name: r.metadata.sensorConfig?.offset ? `Depth: ${r.metadata.sensorConfig.offset}"` : r.metadata.sensorName,
        }))
        return (
          <ChartEditWrapper
            key={"unified-vh400"}
            sigfoxId={sigfoxId}
            sensorPort={sensorPorts.map((s: any) => s.port)}
            sensorName="Soil Moisture (Multiple Sensors)"
            hasHiddenData={unifiedHasHiddenData}
          >
            {(editModeProps) => (
              <UnifiedChartVegetronixVH400
                device={device}
                sensorType={SensorProduct.VegetronixVH400}
                sensorReadings={readings}
                viewport={viewport}
                isLoading={isLoading}
                alertMarkers={offlineAlertMarkers}
                {...editModeProps}
              />
            )}
          </ChartEditWrapper>
        )
      }

      case 'Vegetronix THERM200': {
        const sensorPorts = readings.map((r: any) => ({
          port: r.metadata.sensorName,
          name: r.metadata.sensorConfig?.offset ? `Depth: ${r.metadata.sensorConfig.offset}"` : r.metadata.sensorName,
        }))
        return (
          <ChartEditWrapper
            key={"unified-therm200"}
            sigfoxId={sigfoxId}
            sensorPort={sensorPorts.map((s: any) => s.port)}
            sensorName="Soil Temperature (Multiple Sensors)"
            hasHiddenData={unifiedHasHiddenData}
          >
            {(editModeProps) => (
              <UnifiedChartVegetronixTHERM200
                device={device}
                sensorType={SensorProduct.VegetronixTHERM200}
                sensorReadings={readings}
                viewport={viewport}
                isLoading={isLoading}
                alertMarkers={offlineAlertMarkers}
                {...editModeProps}
              />
            )}
          </ChartEditWrapper>
        )
      }

      case 'Irrometer WM200SS': {
        const sensorPorts = readings.map((r: any) => ({
          port: r.metadata.sensorName,
          name: r.metadata.sensorConfig?.offset ? `Depth: ${r.metadata.sensorConfig.offset}"` : r.metadata.sensorName,
        }))
        return (
          <ChartEditWrapper
            key={"unified-irrometer"}
            sigfoxId={sigfoxId}
            sensorPort={sensorPorts.map((s: any) => s.port)}
            sensorName="Soil Moisture - Irrometer (Multiple Sensors)"
            hasHiddenData={unifiedHasHiddenData}
          >
            {(editModeProps) => (
              <UnifiedChartIrrometer
                device={device}
                sensorType={SensorProduct.IrrometerWM200SS}
                sensorReadings={readings}
                viewport={viewport}
                isLoading={isLoading}
                userUnitSettings={userUnitSettings}
                unitConversions={unitConversions}
                alertMarkers={offlineAlertMarkers}
                {...editModeProps}
              />
            )}
          </ChartEditWrapper>
        )
      }

      case SensorProduct.WindDirection: {
        return (
          <WindDirectionChart
            key={"unified-winddirection"}
            device={device}
            sensorPort={sensorPort}
            readings={readings}
            sensorReadings={readings[0]}
            chartUnits="degrees"
            viewport={viewport}
            userUnitSettings={userUnitSettings}
            unitConversions={unitConversions}
          />
        )
      }

      // A Badger SDI port reaches renderUnifiedCharts once /readings-fast
      // reconstructs its Volume series, giving it the volume + rate pair every
      // other flow meter has. It keeps its own chart rather than joining the
      // FlowMeterChart case below: BadgerSDIChart resolves whichever categories
      // are actually present, so it also covers the single-series fallbacks
      // (legacy volume-only data, or a port whose frequency is unknown).
      case SensorProduct.BadgermeterSDI: {
        const sensorPorts = readings.map((r: any) => r.metadata.sensorName)
        return (
          <ChartEditWrapper
            key={`unified-sdi-${sensorPort}`}
            sigfoxId={sigfoxId}
            sensorPort={sensorPorts}
            sensorName="Flow Meter"
            hasHiddenData={unifiedHasHiddenData}
          >
            {(editModeProps) => (
              <BadgerSDIChart
                device={device}
                sigfoxId={sigfoxId}
                isLoading={isLoading}
                sensorPort={sensorPort}
                sensorProduct={sensorType as SensorProduct}
                sensorReadings={readings}
                viewport={viewport}
                currentTime={currentTime}
                userUnitSettings={userUnitSettings}
                alertMarkers={offlineAlertMarkers}
                {...editModeProps}
              />
            )}
          </ChartEditWrapper>
        )
      }

      case SensorProduct.Badgermeter220BR:
      case SensorProduct.DEMPaddleWheel: {
        const sensorPorts = readings.map((r: any) => ({
          port: r.metadata.sensorName,
          name: r.metadata.productName || r.metadata.sensorName,
        }))
        return (
          <ChartEditWrapper
            key={`unified-flowmeter-${sensorPort}`}
            sigfoxId={sigfoxId}
            sensorPort={sensorPorts.map((s: any) => s.port)}
            sensorName="Flow Meter"
            hasHiddenData={unifiedHasHiddenData}
          >
            {(editModeProps) => (
              <FlowMeterChart
                device={device}
                sigfoxId={sigfoxId}
                isLoading={isLoading}
                sensorPort={sensorPort}
                sensorProduct={sensorType}
                sensorReadings={readings}
                viewport={viewport}
                currentTime={currentTime}
                userUnitSettings={userUnitSettings}
                alertMarkers={offlineAlertMarkers}
                {...editModeProps}
              />
            )}
          </ChartEditWrapper>
        )
      }
    }

    return null
  }

  function containsSensor(sensorType: SensorProduct) {
    return getAllCurrentSensors().includes(sensorType)
  }

  function getAllCurrentSensors() {
    return sensorConfigs
      ? Object.keys(sensorConfigs).map((port) => {
          const data = sensorConfigs[port] as any
          return data.sensorProductName
        })
      : []
  }

  function determineRicMode(deviceConfig: any, readings: any): RicMode {
    let mode = RicMode.Unknown

    if (deviceConfig !== undefined) {
      const deviceMode = Number(deviceConfig.deviceMode)
      mode = RicModeLookup[deviceMode] || RicMode.Unknown
    }

    if (mode === RicMode.Standard || mode === RicMode.Unknown) {
      if (isFlowMeter(readings)) {
        mode = RicMode.FlowMeter
      }
    }

    return mode
  }

  function getAllSensors(readings: any) {
    if (readings === undefined) {
      return []
    }

    return Object.values(readings)
      .map((reading: any) => reading.metadata.productName)
      .filter((sensor) => sensor !== 'Temperature Sensor')
  }

  function isFlowMeter(readings: any) {
    if (readings === undefined) {
      return false
    }

    for (const key of Object.keys(readings)) {
      const reading = readings[key]

      if (reading?.metadata?.productName?.toLowerCase().includes('badgermeter 220br')) {
        return true
      }
      if (reading?.metadata?.productName?.toLowerCase().includes('paddlewheel')) {
        return true
      }
    }

    return false
  }

  // Built once per change of the data it is derived from, NOT on every render.
  // Declared BEFORE the early returns below so the hook is called
  // unconditionally on every render (React rules-of-hooks).
  //
  // The CanvasJS wrapper repaints the entire canvas whenever the `options`
  // object identity changes (its shouldComponentUpdate compares by reference
  // against a value captured at construction, so it is true for every update
  // after the first). Rebuilding this list inline meant each of the ~15 state
  // and context updates that land after mount — readings resolving, sensor and
  // device configs arriving, alert markers, and the admin device list streaming
  // in page by page — produced fresh chart elements, fresh options objects, and
  // a full repaint of every chart on screen. That was the flicker.
  //
  // Returning the same element references lets React skip the chart subtree
  // entirely when nothing they depend on has changed.
  const chartElements: JSX.Element[] = useMemo(
    () => (showBatteryCharts ? renderBatteryCharts(batteryReadings) : renderAllCharts(readings)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      showBatteryCharts,
      batteryReadings,
      readings,
      isLoading,
      loadedWindows,
      sensorConfigs,
      unitConversions,
      deviceConfig,
      showTemperatureCharts,
      viewport,
      userUnitSettings,
      offlineAlertMarkers,
      batteryAlertMarkers,
      // NOT `device`: its identity changes each time a page of the admin device
      // list merges in, but the charts only read deviceName/siteName off it.
      device?.deviceName,
      device?.siteName,
      sigfoxId,
      // `currentTime` is deliberately absent: it is declared in the flow-meter
      // charts' Props but never read by them, so re-deriving the list when it
      // changes would repaint every canvas for a value nothing displays.
    ]
  )

  // Empty state when no device is selected AND not in compare mode
  if (!sigfoxId && !compareMode) {
    return (
      <div className={tailwind.pageContainer}>
        {/* Time range navigation toolbar */}
        {!externalViewport && (
          <div className={tailwind.toolbarContainer}>
            <TimeRangeNavigation
              currentRange={timeRange}
              onRangeChange={handleTimeRangeChange}
              onRefresh={handleRefresh}
              showBatteryCharts={showBatteryCharts}
              onToggleBatteryCharts={handleToggleBatteryCharts}
              showTemperatureCharts={showTemperatureCharts}
              onToggleTemperatureCharts={handleToggleTemperatureCharts}
              showTemperatureToggle={showTemperatureToggle}
              actionsEnabled={false}
              onExport={handleExport}
              exportEnabled={false}
            />
          </div>
        )}

        <div className={tailwind.emptyState}>
          <div className={tailwind.emptyStateContent}>
            <div className={tailwind.emptyStateText}>Select a device to view charts</div>
            <p className={tailwind.emptyStateHelper}>
              Use the device list or map to select a device, or add ?sigfoxId=1A2B3C to the URL
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={tailwind.pageContainer}>
      {/* Time range navigation toolbar */}
      {!externalViewport && (
        <div className={tailwind.toolbarContainer}>
          <TimeRangeNavigation
            currentRange={timeRange}
            onRangeChange={handleTimeRangeChange}
            onRefresh={handleRefresh}
            showBatteryCharts={showBatteryCharts}
            onToggleBatteryCharts={handleToggleBatteryCharts}
            showTemperatureCharts={showTemperatureCharts}
            onToggleTemperatureCharts={handleToggleTemperatureCharts}
            showTemperatureToggle={showTemperatureToggle}
            actionsEnabled={!!sigfoxId && !compareMode}
            onExport={handleExport}
            exportEnabled={!!sigfoxId && !isStillFetching && !compareMode}
            extraActions={
              !!sigfoxId && !compareMode ? (
                <AlertMarkersToggle
                  markerCount={showBatteryCharts ? batteryMarkerPool.length : offlineMarkerPool.length}
                />
              ) : undefined
            }
          />
        </div>
      )}

      {/* Compare Mode Banner */}
      {compareMode && (
        <CompareModeBanner
          selectedCount={selectedDevicesForComparison.length}
          variant={compareVariant}
          onExit={() =>
            toggleCompareMode({
              sigfoxId: sigfoxId || undefined,
              timeRange: { from: timeRange.from, to: timeRange.to },
            })
          }
        />
      )}

      {/* Compare Mode: Sensor Grid Selector. When mixed mode is available we
          render from 1 device so the mode toggle stays reachable; the grid
          itself prompts for more devices when the active mode needs them. */}
      {compareMode && selectedDevicesForComparison.length >= (FEATURE_FLAGS.COMPARE_MIXED_SENSORS ? 1 : 2) && (
        <SensorCompareGrid
          deviceSensorInfos={deviceSensorInfos}
          selectedSensors={selectedSensors}
          selectedSensorType={selectedSensorType}
          selectedSubType={selectedSubType}
          compareVariant={compareVariant}
          showModeToggle={FEATURE_FLAGS.COMPARE_MIXED_SENSORS}
          onToggleSensor={toggleSensorWithType}
          onToggleSensorMixed={toggleSensorForDevice}
          onBulkToggleType={toggleSensorsBulk}
          onSelectSensorType={setSelectedSensorType}
          onSelectSubType={setSelectedSubType}
          onSwitchSensorType={switchSensorType}
          onSetVariant={setCompareVariant}
          onClearAll={clearSensorSelections}
        />
      )}

      {/* Normal Mode: Device Details */}
      {!compareMode && (
        <div className={tailwind.contentContainer}>
          <DeviceDetails device={device} />
          <DeviceSiteDetails device={device} />
          {FEATURE_FLAGS.DEVICE_CONTROL && <DeviceControlPanel sigfoxId={sigfoxId} />}

          {sensorConfigs && Object.keys(sensorConfigs).length > 0 ? (
            <DeviceSensorSettings sigfoxId={sigfoxId} sensorConfigs={sensorConfigs} />
          ) : null}
          {containsSensor(SensorProduct.VegetronixVH400) ? (
            <DeviceAlertSettings sigfoxId={sigfoxId} globalAlertSettings={globalAlertSettings} />
          ) : null}
        </div>
      )}

      {/* Loading spinner - only while there is nothing on screen yet */}
      {isLoading || (compareMode && isCompareReadingsLoading) ? (
        <div className={tailwind.spinner}>
          <Spinner />
        </div>
      ) : null}

      {/* Charts are already drawn, but later time windows are still arriving */}
      {!compareMode && !isLoading && isStillFetching && (
        <div className={tailwind.loadingMore}>
          <Spinner size={16} color="gray" />
          <span>
            Loading more data
            {totalWindows > 1 ? ` — ${Math.min(loadedWindows + 1, totalWindows)} of ${totalWindows}` : ''}
          </span>
        </div>
      )}

      {/* Compare Mode: Comparison Chart */}
      {showCompareView && !isCompareReadingsLoading && (
        <div className={tailwind.chartsContainer}>
          <div className={tailwind.chart}>
            <ComparisonChart
              sensorType={selectedSensorType as ComparableSensorType}
              subType={selectedSubType}
              deviceSensorData={deviceSensorData}
              viewport={viewport}
              isLoading={isCompareReadingsLoading}
              variant={compareVariant}
              layout={compareChartView.layout}
              onSplitOut={compareChartView.splitOut}
              onMoveInto={compareChartView.moveInto}
              onMoveToNewChart={compareChartView.moveToNewChart}
              onRemoveChart={compareChartView.removeChart}
              onCombineAll={compareChartView.combineAll}
              onSeparateAll={compareChartView.separateAll}
              zoomWindow={compareChartView.zoomWindow}
              onZoomChange={compareChartView.setZoomWindow}
              showSplitNotice={compareChartView.showSplitNotice}
              onDismissNotice={compareChartView.dismissNotice}
            />
          </div>
        </div>
      )}

      {/* Normal Mode: Charts */}
      {!compareMode && (
        <div className={tailwind.chartsContainer}>
          {sigfoxId && device && isLoading === false
            ? chartElements.map(
                (chart, index) => {
                  // Keyed by the chart's own key, not its position. The list
                  // changes shape while the page loads (placeholders give way to
                  // real charts, the RIC mode settles once the device config
                  // lands), so positional keys pointed at a different chart each
                  // time and React remounted them — which is what made the charts
                  // visibly redraw and refire their per-chart config requests.
                  return (
                    <div key={chart.key ?? index} className={tailwind.chart}>
                      {chart}
                    </div>
                  )
                }
              )
            : null}
        </div>
      )}

      {/* Test caching component - at the bottom */}
      {sigfoxId && !isStillFetching && (
        <div className={tailwind.contentContainer}>
          <TestReadingsCache
            sigfoxId={sigfoxId}
            from={Math.round(viewport.minimum / 1000)}
            to={Math.round(viewport.maximum / 1000)}
          />
        </div>
      )}

      {/* Export Modal */}
      {device && (
        <ExportModal
          isOpen={isExportModalOpen}
          onClose={handleCloseExportModal}
          device={device}
          currentTimeRange={timeRange}
          sensorConfigs={sensorConfigs}
          readingsData={readingsData}
          batteryData={batteryData}
          devices={devices}
        />
      )}
    </div>
  )
}

const tailwind = {
  pageContainer: 'flex flex-col h-full space-y-4',
  toolbarContainer: 'px-4 py-3 bg-gray-50 border-b border-gray-100',
  contentContainer: 'mx-1',
  chartsContainer: 'px-4',
  chart: 'mb-6',
  loadingMore:
    'mx-4 flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700',
  spinner: 'absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50',
  buttonEnabled:
    'bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-4 rounded focus:outline-none focus:shadow-outline mb-0',
  buttonDisabled: 'bg-gray-400 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mb-3',
  alert: 'bg-red-500 text-white font-bold rounded px-4 py-2 mb-10 mt-6 h20 w-[320px] mx-auto text-center',
  emptyState: 'bg-white rounded-lg shadow p-6 mx-4',
  emptyStateContent: 'text-center py-12',
  emptyStateText: 'text-gray-500 text-lg',
  emptyStateHelper: 'text-sm text-gray-400 mt-2',
}
